/**
 * FieldZonePlaceholder (Inventory Screens Steps 4.1–5.1)
 * -------------------------------------------------------
 * The field-zone shell. 5.1 gives it Bob's showView model: in-zone view
 * state 'sites' (default) | 'records' — NO /field sub-routes (the field SW
 * scope guardrail). Read-only in 5.1: the records view renders the INV_VIEW
 * grid; editing/producers are 5.2+.
 *
 * Site tap → fetchFieldRecords fan-out (paging walk) → records-only persist
 * (snapshot echoed — never nulls identity/profile) → records view from the
 * IDB read-back. Offline (gate mode or dead network): cached records serve
 * when present; a failed load always lands back on a usable sites view.
 *
 * Still the field SW's lazy registration point. The mode / identity /
 * site-count diagnostics are GONE (F-38): 'Mode' rendered useFieldLoad's
 * status, which is set by ANY fetch throw (500, CORS, an auth rejection)
 * and so claimed a network state it could not know.
 * ⚠️ F-41, knowingly accepted: that same line ALSO carried
 * useFieldAuth's `offline` — an ACCURATE fact (no MSAL account, so the
 * zone is rendering from the device snapshot; FieldProtectedRoute:89) that
 * is now rendered NOWHERE. FieldSyncChip cannot stand in for it: its
 * offline branch reads navigator.onLine, so a rep in snapshot mode on live
 * wifi sees no indication at all. Traded a line that lies for a line that
 * is missing — a net gain, but a trade. Queue state surfaces
 * through FieldSyncChip (fixed, above BOTH views — the records view had no
 * sync surface before 5.4 A-1) + the read-only FieldSyncSheet on chip tap
 * (own tmgFieldSyncSheet sentinel, same D-5.2-A/D-5.3-8 router-native
 * mechanism). The old field-queue-counts debug line is retired — the chip
 * replaces it.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { registerFieldServiceWorker } from '../../../registerFieldSW';
import { useFieldAuth } from '../auth/FieldProtectedRoute';
import { useFieldLoad } from '../data/useFieldLoad';
import { useFieldDrain } from '../sync/useFieldDrain';
import { useFieldQueue } from '../sync/useFieldQueue';
import { hasAnyToolCreate } from '../sync/fieldOutbox';
import { isPendingRecord } from '../sync/pendingId';
import { loadSiteRecords } from '../data/fetchFieldRecords';
import {
  getPruneDryRun, readPruneDryRunOptIn, writePruneDryRunOptIn, registerOptInTap,
} from '../data/pruneDryRun';
import { buildDuplicateDraft } from '../data/duplicateRecord';
import { useLiveQuery } from 'dexie-react-hooks';
import { fieldDb } from '../db/fieldDb';
import { getSites, getRecords, isDirtyRecord } from '../db/fieldStore';
import { tapFlag } from '../data/flagTap';
import { pinKey, makePin, pinnedRows, pinnedTree, remapPin } from '../data/rowPin';
import { recordLabel } from '../recordLabel';
import useFieldHiddenFields from '../hooks/useFieldHiddenFields';
import useFieldSortState from '../hooks/useFieldSortState';
import useFieldFilterState from '../hooks/useFieldFilterState';
import useFieldGroupState, { groupCount } from '../hooks/useFieldGroupState';
import sortRecords from '../data/sortRecords';
import filterRecords from '../data/filterRecords';
import groupRecords from '../data/groupRecords';
import { fieldsForView, withChildColumns } from '../data/editableFields';
import { useUser } from '../../../shared/context/UserContext';
import FieldSitesList from '../components/FieldSitesList';
import FieldRecordsList, { visibleColumnsFor, EMPTY_COLLAPSED } from '../components/FieldRecordsList';
import FieldRecordModal from '../components/FieldRecordModal';
import FieldColumnsModal from '../components/FieldColumnsModal';
import FieldSearchOverlay from '../components/FieldSearchOverlay';
import FieldSyncChip from '../components/FieldSyncChip';
import FieldSyncSheet from '../components/FieldSyncSheet';
import FieldViewPanel from '../components/FieldViewPanel';
import FieldSortModal from '../components/FieldSortModal';
import FieldFilterModal from '../components/FieldFilterModal';
import FieldGroupModal from '../components/FieldGroupModal';
import { BUILD_SHA, BUILD_DIRTY } from '../../../buildInfo';

// B-224: the banner over the records when a failed Load served the saved copy.
// It states the rep's situation, not the cause: no err.message (console.error
// keeps that), and "couldn't refresh" rather than "couldn't reach the server",
// because the fallback also catches a 4xx/5xx, unparseable JSON and a local
// persist or read-back failure, none of which is an unreachable server.
const STALE_RECORDS_COPY = "Saved copy — couldn't refresh. May be out of date.";

// ── Wave 1 Phase 4A: what each Unsent row IS — the ONE discriminator ──────
// In the host, not the sheet: Phase 3 reads the same `state` for its copy,
// and a second copy of this ladder would drift from the first. Exactly one
// state per row, first match wins:
//   'cross_site' — not the open site. FIRST, over everything: nothing on such
//                  a row can be acted on from here (the same comparison the
//                  sheet's tappability rule already makes).
//   'orphan'     — pending- row, NO tool_create in any status. Nothing will
//                  ever send it. The only state that gets Send.
//   'waiting'    — pending- row whose tool_create exists, in any status.
//   'in_flight'  — synced row with a field_capture in the queue, in any
//                  status (status-blind, like 'waiting').
//   'other'      — anything else; renders exactly as it did before 4A.
function unsentRowState(row, queueItems, activeSiteId) {
  if (row.site_id !== activeSiteId) return 'cross_site';
  if (isPendingRecord(row.record_id)) {
    return hasAnyToolCreate(queueItems, row.record_id) ? 'waiting' : 'orphan';
  }
  if (queueItems.some((i) => i.type === 'field_capture' && i.record_id === row.record_id)) {
    return 'in_flight';
  }
  return 'other';
}

// readUnsent's per-row projection (F-45), at module scope so it can be tested.
// A pending- row also carries `create`: the two fields enqueueToolCreate needs
// that the base projection lacks (site_id is already there). Read from
// WORKING, where the editable fields live (recordLabel.js). OEM has had a
// picker since OEM arc Phase 2, so a zone-created row may hold one: it rides
// here as picked, or null when none was chosen. (The modal's own create still
// sends null and lets the post-create follow-up capture carry a picked OEM.)
// Whether `create` reaches the sheet is toUnsentItem's decision, not this one's.
export function projectUnsentRow(r) {
  const row = { record_id: r.record_id, site_id: r.site_id, label: recordLabel(r) };
  if (isPendingRecord(r.record_id)) {
    row.create = {
      supplier_tool_id: r.working?.supplier_tool_id ?? null,
      oem: r.working?.oem ?? null,
    };
  }
  return row;
}

// One projected row → the item the sheet renders. The create fields ride on
// 'orphan' ONLY: no other state has a Send, so no other item carries them.
export function toUnsentItem(row, queueItems, siteNameById, activeSiteId) {
  const { create, ...base } = row;
  const state = unsentRowState(base, queueItems, activeSiteId);
  return {
    ...base,
    site_name: siteNameById.get(base.site_id) || base.site_id,
    state,
    ...(state === 'orphan' ? create : {}),
  };
}

export default function FieldZonePlaceholder() {
  // LOAD-BEARING, do not delete as unused: this destructure is what makes
  // connectivity REACTIVE for the chip and the sheet. Subscribing to the
  // context is what re-renders this component when FieldProtectedRoute's
  // online/offline listener flips networkDown, and the chip re-reads
  // navigator.onLine only because that re-render happens. `offline` itself
  // renders nowhere (F-41, see the header) — which is exactly why this line
  // looks like dead code and is not.
  const { offline, networkDown } = useFieldAuth();
  const { callerEmail } = useUser();
  // identity dropped from this destructure with the F-38 deletion below:
  // its ONLY reader was the field-identity line. status and siteCount stay
  // (both feed the sites-read effect at :60/:66), and so does error (the
  // field-error surface, which is a real error path, not a diagnostic).
  const { status, siteCount, error } = useFieldLoad();
  // P2-2 B: reconcile's re-keyed rows reach the grid through applyRekeyed,
  // declared below beside handleRecordChange. The drain hook has to be
  // called here, above `records`, so it reaches the handler through a ref.
  const applyRekeyedRef = useRef(null);
  const queue = useFieldDrain({ onRekeyed: (pairs) => applyRekeyedRef.current?.(pairs) });
  const location = useLocation();
  const navigate = useNavigate();

  // The INV/ALL field view, lifted here so it persists record-to-record
  // within a session. Set from the records grid's header (Bob P1 item 2a);
  // the grid, the search overlay, the Columns/Sort/Filter/Group pickers and
  // the record form all read it. NAME: `view` is this component's shell view
  // ('sites' | 'records', derived below), so this one is fieldView —
  // reusing `view` would shadow the shell's own. Session-scoped on purpose:
  // NOT localStorage, so a rep's next shift starts on INV.
  const [fieldView, setFieldView] = useState('inv'); // 'inv' | 'all'
  const [sites, setSites] = useState([]);
  const [activeSite, setActiveSite] = useState(null);
  const [records, setRecords] = useState([]);
  // B-254 Phase 3: the pinned row set (data/rowPin.js). null, or the view frozen on the first
  // flag tap: its key and record_ids. It applies only while the view key still matches; see
  // shownRecords below.
  const [pin, setPin] = useState(null);
  // Ghost prune Phase A1: what the latest ONLINE Load would have pruned, shown
  // under the record count. Null after an offline entry or a fallback — no
  // Load ran, so there is nothing to report.
  const [pruneDryRun, setPruneDryRun] = useState(null);
  // A1.5: the header line shows only where it was turned on (five taps on the
  // Build stamp). Off by default; see pruneDryRun.js.
  const [pruneDryRunOptIn, setPruneDryRunOptIn] = useState(readPruneDryRunOptIn);
  const stampTapsRef = useRef([]);
  const handleStampTap = useCallback(() => {
    const { taps, toggle } = registerOptInTap(stampTapsRef.current, Date.now());
    stampTapsRef.current = taps;
    if (!toggle) return;
    const next = !pruneDryRunOptIn;
    writePruneDryRunOptIn(next);
    setPruneDryRunOptIn(next);
  }, [pruneDryRunOptIn]);
  const [loadingSiteId, setLoadingSiteId] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // B-224: the rows on screen are the saved copy, served by handleSelectSite's
  // fallback after this site's Load failed. Null, or the banner's text.
  // Cleared at the start of every site entry beside loadError, so it goes when
  // the next Load succeeds; no dismiss control, which would put the rep back in
  // the silent state with one tap. Backing out to the sites list leaves it set.
  // NOT set on an offline (State 2b) entry, by decision, although those rows
  // come from the cache too: the chip already reads Offline there, and a
  // banner on every routine offline session is one the rep stops reading.
  const [staleRecords, setStaleRecords] = useState(null);
  // P2-2 B: the site the grid is showing, written SYNCHRONOUSLY beside every
  // setActiveSite. applyRekeyed runs from an async drain cycle and has to
  // compare against the site as of now, not as of the last render.
  const liveSiteIdRef = useRef(null);
  // P2-2 B, load race: null when no site load is in flight, an array while
  // one is. A re-key landing mid-load would be overwritten by the load's
  // wholesale setRecords, so it is held here and replayed right after it.
  const rekeyStashRef = useRef(null);
  // P2-2 B, open modal: the record id the record modal is on, and AT MOST ONE
  // re-key held back because it would replace that row. modalRecord is
  // records.find(modalRecordId), so replacing the row under an open modal
  // makes it return null with its history sentinel still pushed. Written
  // SYNCHRONOUSLY at the two pushes that open a record (handleRowTap,
  // handleUnsentTap), so a re-key landing before the open renders is already
  // held. Cleared by the modal-id effect beside applyRekeyed, NOT at a close
  // handler — the liveSiteIdRef split, for the same reason: hardware and
  // gesture back run no code of ours, and a clear ahead of the pop would let a
  // re-key replace the row while the modal is still on screen.
  const modalOpenIdRef = useRef(null);
  const deferredRekeyRef = useRef(null);

  // ── Shell view: DERIVED, not stored (Unit D) ────────────────────────────
  // Was `const [view, setView] = useState('sites')`, co-set with
  // setActiveSite at all three call sites. It is a second source of truth
  // for a fact activeSite already carries, and Unit D makes that a
  // liability: a history pop can now clear activeSite from OUTSIDE this
  // component's own handlers, so any path that cleared one and not the
  // other would render a records screen with no site. Declared here rather
  // than above fieldView because it reads activeSite, which is declared
  // above it — the old useState had no such ordering constraint.
  const view = activeSite ? 'records' : 'sites';

  // ── The SITE sentinel (13th), router-native — Unit D ────────────────────
  // Bob walkthrough 2026-09-03 item 3: on-screen Android back inside a site
  // exited the field zone entirely instead of returning to the site list.
  // Cause: site selection pushed no history entry and the ‹ Sites button
  // popped none, so the sites<->records transition was invisible to history
  // and the platform back affordance popped the /field entry itself.
  //
  // THIS IS THE BOTTOM SENTINEL OF THE STACK. It is pushed first, on site
  // entry, so all twelve overlay sentinels layer above it; each of their
  // pushes spreads location.state, which carries this key forward onto the
  // pushed entry. See the rewritten note on the Columns block for why that
  // means an overlay pop still cannot move the site view.
  //
  // Declared UP HERE, unlike the other twelve (which live together at :420+),
  // because pushSiteSentinel is called from handleSelectSite below and would
  // otherwise be a TDZ reference.
  const siteOpen = location.state?.tmgFieldSiteOpen === true;

  // THE VALUE IS A BOOLEAN, NOT THE SITE_ID, AND THAT IS DELIBERATE.
  // A boolean is all either effect below needs, and it matches the nine other
  // boolean sentinels. Carrying site_id would add nothing and would actively
  // invite a future reader to "improve" the orphan heal into an auto-reload —
  // which is forbidden: that fires an unrequested loadSiteRecords on a handset
  // that may be offline, to restore a screen the rep may have left on purpose.
  const pushSiteSentinel = useCallback(() => {
    if (siteOpen) return; // already pushed — never double-push the sentinel
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldSiteOpen: true },
    });
  }, [navigate, location.pathname, location.search, location.state, siteOpen]);

  // ── Teardown: the FIRST effect in this zone keyed on a sentinel
  //    DISAPPEARING rather than appearing ─────────────────────────────────
  // handleBack is now navigate(-1), so ‹ Sites and Android hardware back are
  // ONE code path: both pop the site entry, the sentinel goes away, and this
  // effect does the teardown handleBack used to do inline. Nothing else in
  // this file clears activeSite.
  //
  // EDGE-TRIGGERED, NOT LEVEL-TRIGGERED, and that is the whole safety
  // argument. A level test (`!siteOpen && activeSite`) would fire on ENTRY in
  // any render where the sentinel had not yet landed beside setActiveSite,
  // tearing the screen down the instant it opened. Comparing against the
  // PREVIOUS value cannot: on entry the transition is false->true, which is
  // not a teardown edge no matter how the two updates batch. React 18's
  // createRoot does auto-batch them (the push sits in the same task as
  // setActiveSite, after the await) — this makes that a belt, not the brace.
  //
  // `records` is cleared too, not just activeSite: a stale 841-row snapshot
  // surviving into the sites view would also survive into handleUnsentTap's
  // records.find, which is how a cross-site row becomes tappable.
  const prevSiteOpenRef = useRef(false);
  useEffect(() => {
    const was = prevSiteOpenRef.current;
    prevSiteOpenRef.current = siteOpen;
    if (was && !siteOpen) {
      setActiveSite(null);
      setRecords([]);
      setPin(null); // B-254: re-entering the same site must not revive a pin of stale ids
      liveSiteIdRef.current = null;
      deferredRekeyRef.current = null; // the grid it was held for is gone
    }
  }, [siteOpen]);

  // ── Orphan heal ─────────────────────────────────────────────────────────
  // BrowserRouter keeps location.state in history.state, so the browser
  // restores every sentinel across a full reload — but activeSite is useState
  // and does not. Reload inside a site therefore lands on the sites list with
  // tmgFieldSiteOpen still set: a sentinel with nothing under it. Forward
  // after a back reaches the same state.
  //
  // LEVEL-TRIGGERED, deliberately the opposite of the teardown above. A
  // reload has no previous render to compare against, so an edge detector
  // could never observe the transition into this state and would leave the
  // orphan standing.
  //
  // STRIPS THE SENTINEL, DOES NOT RE-LOAD THE SITE — see the boolean note
  // above for why re-entering is the wrong instinct.
  //
  // replace, NOT push: the orphan IS the current entry. A push would leave it
  // behind and the next back would land on it again, healing in a loop.
  // replace rewrites it in place, so forward cannot resurrect it either.
  //
  // loadingSiteId GUARD is belt-and-suspenders. The push lands after the
  // await, so mid-load the sentinel is absent and this condition is already
  // false; the guard is what stops a future edit that moved the push earlier
  // from turning this effect into a mid-fetch sentinel stripper.
  useEffect(() => {
    if (siteOpen && !activeSite && loadingSiteId == null) {
      const { tmgFieldSiteOpen, ...rest } = location.state || {};
      navigate(location.pathname + location.search, { state: rest, replace: true });
    }
  }, [siteOpen, activeSite, loadingSiteId, navigate,
      location.pathname, location.search, location.state]);

  useEffect(() => {
    registerFieldServiceWorker();
  }, []);

  // Site cards from IDB once the Load settles (and again if it re-runs).
  useEffect(() => {
    if (status === 'loading') return undefined;
    let alive = true;
    getSites()
      .then((rows) => { if (alive) setSites(rows); })
      .catch((err) => console.error('[fieldZone] sites read failed:', err));
    return () => { alive = false; };
  }, [status, siteCount]);

  // P2-2 B: close the load's stash window and replay what landed during it.
  // Called right after the load's setRecords, in the same batch, so the
  // replay's updater sees the loaded rows. Replaying a pair the load already
  // carries is a no-op (it replaces by real id in place), so this is safe.
  const flushRekeyStash = useCallback(() => {
    const stashed = rekeyStashRef.current;
    rekeyStashRef.current = null;
    applyRekeyedRef.current?.(stashed);
  }, []);

  const handleSelectSite = useCallback(async (site) => {
    setLoadingSiteId(site.site_id);
    setLoadError(null);
    setStaleRecords(null);
    setPruneDryRun(null);
    rekeyStashRef.current = []; // P2-2 B: hold re-keys until this load lands
    try {
      let rows;
      if (offline) {
        // Offline gate mode: never touch the network — cached rows or bust.
        rows = await getRecords(site.site_id);
        if (rows.length === 0) {
          throw new Error('No cached records on this device for that site.');
        }
      } else {
        rows = await loadSiteRecords(site.site_id, callerEmail);
        setPruneDryRun(getPruneDryRun(site.site_id));
      }
      setActiveSite(site);
      setRecords(rows);
      liveSiteIdRef.current = site.site_id;
      flushRekeyStash();
      // SAME BATCH as setActiveSite, and the batch matters more than the
      // order: React 18's createRoot auto-batches every update in one task,
      // including after an await, so activeSite and the sentinel reach the
      // render together. The teardown effect is edge-triggered so that a
      // batching failure could not tear this screen down on entry anyway —
      // but the push belongs here, AFTER the await, and not at the top of
      // this function: pushed before the fetch it would sit present with
      // activeSite still null for the whole network round trip, which is
      // exactly the orphan shape the heal effect strips.
      pushSiteSentinel();
    } catch (err) {
      console.error('[fieldZone] site records load failed:', err);
      // Dead-network fallback while nominally online: serve the cache if any.
      let cacheErr = null;
      try {
        const cached = await getRecords(site.site_id);
        if (cached.length > 0) {
          setActiveSite(site);
          setRecords(cached);
          setStaleRecords(STALE_RECORDS_COPY); // B-224: say so, over the records
          liveSiteIdRef.current = site.site_id;
          flushRekeyStash();
          pushSiteSentinel(); // same batch as setActiveSite — see the success arm
          return;
        }
      } catch (readErr) {
        cacheErr = readErr;
        console.error('[fieldZone] cache fallback failed:', readErr);
      }
      // Name the failure that left the screen empty: the Load's when the cache
      // simply held no rows, the cache read's when that read itself threw.
      const siteLabel = site.site_name || site.site_id;
      setLoadError(cacheErr
        ? `Couldn't load ${siteLabel} or read its saved copy: ${cacheErr.message}`
        : `Couldn't load ${siteLabel}: ${err.message}`);
    } finally {
      setLoadingSiteId(null);
      // P2-2 B: a failed load showed nothing, so its stash is dropped; those
      // rows come from Dexie on the next site entry. A no-op after a flush.
      rekeyStashRef.current = null;
    }
    // pushSiteSentinel joins the deps, so this callback is re-created on every
    // navigation. That is the correct trade and it costs nothing measurable:
    // it is only passed to FieldSitesList as onSelect, and that component is
    // not memoised, so it re-renders regardless. The alternative — omitting it
    // — would capture the location.state of the render that built this
    // callback, which for a handler that pushes AFTER an await is the state
    // from before the fetch.
  }, [offline, callerEmail, pushSiteSentinel, flushRekeyStash]);

  // -- F-43: which VISIBLE rows still hold unsent edits --------------------
  // The `records` array above stays a one-shot snapshot ON PURPOSE. Two
  // things depend on the host list NOT being live: handleRecordChange's
  // upsert, and the deliberate non-push of a half-finished pending-* create
  // row (FieldRecordModal, the "NO onRecordChange here" note in
  // handleCreateInner's PARTIAL branch, where an eyeball proved that exposing
  // it makes it tappable and kills the resume payloads). So only the dirty
  // FLAG goes live — a Set of record_id, never the rows themselves.
  //
  // Why it has to be live at all: reconcileConfirmations rewrites
  // record.dirty (since PN-STALE B2 Phase 3 it removes only the flags the
  // confirmed item settled) and commits it with fieldDb.records.put without
  // ever touching React. dirtyCount (P2) IS live, so after a drain
  // the chip flipped to 'Synced' while snapshot-derived row tints stayed
  // orange — the two surfaces contradicted each other. This closes that.
  //
  // ZONE CONTRACT — identical to useFieldQueue.js:8-23: the Dexie read
  // starts SYNCHRONOUSLY. No await, no async wrapper, no try/catch ahead of
  // it, and deliberately NOT through fieldStore.getRecords — that helper's
  // `await ensureOpen()` hop would register zero dependencies and freeze
  // this Set at its mount value, which is the exact bug being fixed.
  //
  // Cost: scoped to the open site and INDEX-SERVED — site_id is indexed
  // (fieldDb.js:39), so this is a keyed range read, not dirtyCount's
  // unavoidable full scan. The filter deserialises each row in that range
  // to test the predicate but COLLECTS only the dirty ones, so the array
  // this materialises is bounded by dirt, not by site size.
  //
  // isDirtyRecord is imported, never re-stated — one predicate shared with
  // countDirtyRecords and the dirtyCount querier, so no two of the three
  // can disagree about what 'dirty' means.
  const activeSiteId = activeSite?.site_id ?? null;
  const readDirtyIds = () => fieldDb.records
    .where('site_id').equals(activeSiteId)
    .filter(isDirtyRecord)
    .toArray()
    .then((rows) => new Set(rows.map((r) => r.record_id)));
  const dirtyIds = useLiveQuery(
    () => {
      if (!activeSiteId) return new Set();
      return fieldDb.isOpen() ? readDirtyIds() : fieldDb.open().then(readDirtyIds);
    },
    [activeSiteId],
    new Set(),
  );

  // ── F-45: the DEVICE-WIDE unsent set, for the sync sheet's enumeration ──
  // A SECOND querier deliberately sitting alongside the site-scoped one above,
  // not a widening of it. The grid needs "which rows in the open site are
  // tinted" and must stay index-served on site_id; the sheet needs "every
  // record anywhere holding unsent edits", because a rep dirties records
  // across sites in one shift and the count the sheet has always shown was
  // already device-wide (useFieldDrain's dirtyCount). Merging the two would
  // either cost the grid its index or make the sheet lie by omission.
  //
  // UNINDEXED FULL SCAN, unavoidably — `dirty` is a nested object and
  // key-presence inside one is not indexable (fieldStore.js:369-371). What
  // keeps it survivable is the PROJECTION in the .then: each row carries
  // baseline + working + child arrays + media[], and countDirtyRecords'
  // docstring (:374-377) is explicit that never materialising those is the
  // whole reason its scan is affordable. .filter() collects only dirty rows,
  // so the array is bounded by dirt; the projection (projectUnsentRow) then
  // drops everything except the three fields the sheet renders, plus two
  // create fields on pending- rows (Phase 4A).
  //
  // Zone-clean shape, same as every live querier in the zone: the read starts
  // SYNCHRONOUSLY, and the closed-DB branch chains off fieldDb.open(), a
  // DexiePromise whose .then stays inside Dexie's tracking zone. Never
  // ensureOpen() here — that native-promise hop is the shipped 5.4 bug that
  // froze subscriptions at their mount snapshot.
  const readUnsent = () => fieldDb.records
    .filter(isDirtyRecord)
    .toArray()
    .then((rows) => rows.map(projectUnsentRow));
  const unsentRows = useLiveQuery(
    () => (fieldDb.isOpen() ? readUnsent() : fieldDb.open().then(readUnsent)),
    [],
    [],
  );

  // Phase 4A: the queue, for unsentRowState. The zone's shared live
  // subscription (useFieldQueue) — never a hand-rolled querier. useFieldDrain
  // above already reads it but returns only per-status counts, so the host
  // takes its own subscription rather than widening that hook's return.
  const queueItems = useFieldQueue();

  // Site NAMES come from the `sites` state, which getSites() fills from
  // fieldDb.sites — that store holds EVERY site the rep can see, not just the
  // loaded one (persistLoadResponse bulkPuts the whole get_field_sites
  // response, fieldStore.js:166/191). That is what makes a cross-site unsent
  // row nameable at all. Falls back to the raw site_id rather than blank: an
  // ugly identifier still tells the rep which site to open.
  const siteNameById = useMemo(() => {
    const m = new Map();
    for (const s of sites) m.set(s.site_id, s.site_name);
    return m;
  }, [sites]);

  // Same-site rows FIRST — those are the only ones the rep can act on from
  // here — then grouped by site name, then by label. Ordering only; the set
  // itself is whatever the querier returned. Each item also gets its Phase 4A
  // `state` (toUnsentItem → unsentRowState).
  const unsentItems = useMemo(() => {
    const withNames = unsentRows.map(
      (r) => toUnsentItem(r, queueItems, siteNameById, activeSiteId),
    );
    return withNames.sort((a, b) => {
      const aHere = a.site_id === activeSiteId ? 0 : 1;
      const bHere = b.site_id === activeSiteId ? 0 : 1;
      if (aHere !== bHere) return aHere - bHere;
      if (a.site_name !== b.site_name) return a.site_name < b.site_name ? -1 : 1;
      return a.label < b.label ? -1 : a.label > b.label ? 1 : 0;
    });
  }, [unsentRows, queueItems, siteNameById, activeSiteId]);

  // ── Unit 4: per-site hidden-column state ────────────────────────────────
  // Phase 2 consumes the whole surface: `hidden` reaches the grid and the
  // Columns modal, the three mutators are the modal's handlers.
  // P1-2 piece 3: per VIEW as well as per site. `hidden` is the current
  // view's set, so every consumer below sees the view on screen.
  const { hidden, toggle, showAll, hideAll } = useFieldHiddenFields(activeSiteId, fieldView);

  // ── Unit 5 Phase 1: per-site sort levels ────────────────────────────────
  // A SIBLING key inside the same per-site object useFieldHiddenFields owns —
  // not a second storage mechanism. clearSort is intentionally not destructured:
  // the Sort modal's Clear is a draft edit, so an empty sort reaches storage
  // through the ordinary setSort/Apply path like any other.
  const { sort, setSort } = useFieldSortState(activeSiteId);

  // ── Unit 5 Phase 2: per-site filter conditions ──────────────────────────
  // Two more sibling keys in the same per-site object, written together in one
  // persistPatch. clearFilter IS destructured here (unlike clearSort): the
  // zero-result surface needs a committed clear with no modal and no Apply.
  const { filter, filterLogic, setFilter, clearFilter } = useFieldFilterState(activeSiteId);

  // ── Unit 5 Phase 3: per-site grouping ───────────────────────────────────
  // A FOURTH sibling key in the same per-site object. clearGroup is not
  // destructured, for the reason clearSort is not: the Group modal's Clear is
  // a draft edit, so an empty grouping reaches storage through the ordinary
  // setGroup/Apply path like any other.
  const { group, setGroup } = useFieldGroupState(activeSiteId);

  // WHAT THE GRID RENDERS. `records` stays the one-shot snapshot the comment at
  // :134-139 describes and every id lookup below still reads it; this is a
  // DERIVED view of it, which is a different thing from making it live. With an
  // empty filter AND an empty sort both helpers return their input BY
  // REFERENCE, so an unfiltered unsorted grid gets the exact array it already
  // had and this memo costs nothing.
  //
  // FILTER FIRST, THEN SORT. Sorting the survivors is strictly less work than
  // ordering 841 rows and discarding most of them, and the two commute safely
  // in this direction only — sortRecords is stable, so it can reorder what
  // filterRecords selected but can never re-admit what it rejected.
  //
  // Deliberately NOT threaded into the search overlay (:522 stays on raw
  // `records`, D-102): search answers "does this tool exist on this site",
  // which must not depend on how the rep happens to have narrowed the grid.
  // A rep who filters to one rack and then searches for a tool that is not in
  // it must still find it.
  const displayedRecords = useMemo(
    () => sortRecords(filterRecords(records, filter, filterLogic), sort),
    [records, filter, filterLogic, sort],
  );

  // ── Unit 5 Phase 3c: the bucket tree the grid renders ───────────────────
  // AFTER displayedRecords and derived FROM it, never beside it. Grouping
  // buckets the rows the rep has ALREADY filtered and sorted, so the pipeline
  // reads filter → sort → group in that order and displayedRecords stays the one
  // definition of "the rows in view".
  //
  // THE group.primary GATE IS LOAD-BEARING (D-150). groupRecords returns its
  // input BY REFERENCE when there is no grouping — that identity is deliberate
  // and its docblock says so — which means calling it unconditionally would hand
  // the grid a `groups` prop that is sometimes a bucket tree and sometimes the
  // flat records array, and the grid would have to TYPE-SNIFF to tell the two
  // apart. `null` is an unambiguous "no grouping"; a flat array is not.
  const groupTree = useMemo(
    () => (group.primary ? groupRecords(displayedRecords, group) : null),
    [displayedRecords, group],
  );

  // ── B-254 Phase 3: the pin (spec v1.1 §4; data/rowPin.js has the traps) ─────
  // A flag tap under a filter, sort or group on that flag would move or remove the row it just
  // ticked. The first tap freezes the view on screen; the grid then shows that pinned set, by
  // id, from the live `records`. It applies only while the view key matches, so a filter, sort
  // or group change, the INV/ALL switch or another site releases it in the same render, with
  // no effect and no stale frame. Leaving the site and a create (D-10) release it explicitly.
  // It holds through an open record and every overlay: none of them changes the key.
  const viewKey = useMemo(
    () => pinKey({ siteId: activeSiteId, fieldView, filter, filterLogic, sort, group }),
    [activeSiteId, fieldView, filter, filterLogic, sort, group],
  );
  const pinActive = pin !== null && pin.key === viewKey;
  const shownRecords = useMemo(
    () => (pinActive ? pinnedRows(pin, records) : displayedRecords),
    [pinActive, pin, records, displayedRecords],
  );
  const shownTree = useMemo(
    () => (pinActive ? pinnedTree(pin, records) : groupTree),
    [pinActive, pin, records, groupTree],
  );
  // What the first tap freezes: the view as derived, which is what is on screen when no pin
  // applies. Assigned during render, the queryRef pattern, so handleFlagTap stays stable.
  const pinSourceRef = useRef(null);
  pinSourceRef.current = { key: viewKey, rows: displayedRecords, tree: groupTree };

  // WHICH BUCKETS ARE CLOSED — host useState, and deliberately none of the
  // three things it could have been (D-147/D-148):
  //
  //   NOT A ROUTER SENTINEL. The twelve sentinels in this file all exist for
  //   ANDROID HARDWARE BACK: each is a LAYER a rep expects back to close. A
  //   collapsed bucket is not a layer, and making it one would mean a rep who
  //   closed four buckets needs four backs to leave the grid.
  //
  //   NOT PERSISTED, AND NOT AN FSG KEY. The four keys in that per-site object
  //   are deliberate configuration a rep should not re-make each shift; which
  //   buckets they happened to close while walking a rack is not. Writing it
  //   would also put a fifth writer on an object whose whole design is that its
  //   writers never surprise each other.
  //
  //   NOT LIST-LOCAL. The reset below belongs to whoever owns activeSiteId, and
  //   that is this file.
  //
  // KEYS ARE OPAQUE HERE. FieldRecordsList builds them — a level-1 key is the
  // bucket value, a level-2 key joins parent and child with a NUL — and this
  // file never parses one. It stores strings and compares them.
  const [collapsedGroups, setCollapsedGroups] = useState(EMPTY_COLLAPSED);

  // IMMUTABLE, always: a NEW Set from the old, then add or delete. The
  // alternative — prev.add(key) and return prev — mutates the very object React
  // is holding as current state, so the new value compares equal to itself,
  // React bails out, and the grid does not move while the Set quietly changed
  // underneath it.
  const handleToggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  // Open a different site → nothing is collapsed. Collapse keys are field
  // VALUES, so a key carried across sites either matches nothing or, worse,
  // matches a same-named bucket on the new site and opens it already closed.
  //
  // A NEW EFFECT, reported as such: this file has no existing effect keyed on
  // activeSiteId to hang the reset on. The site-scoped subscriptions here are
  // useLiveQuery (:178, :218), and each sibling FSG hook owns its own
  // ref-guarded re-read inside the hook. No ref guard is needed here: on mount
  // this sets state to the reference it already holds, and React bails out of a
  // setState to an identical value, so the mount run costs nothing rather than
  // spending a wasted render.
  useEffect(() => {
    setCollapsedGroups(EMPTY_COLLAPSED);
  }, [activeSiteId]);

  // NO DISPOSAL GATE ANY MORE (Unit F, 2026-09-04) — the second of the two.
  // `marked` was the only member of DISPOSAL_ONLY_FIELDS and moved into
  // BASE_FIELDS, so it is now offered on EVERY site rather than DISPOSAL ones
  // only. The branch this memo used to carry —
  // `isDisposalSite ? [...EDITABLE_FIELDS, ...DISPOSAL_ONLY_FIELDS] :
  // EDITABLE_FIELDS` — went inert the moment that array emptied, and with it
  // the `isDisposalSite` derivation that existed solely to feed it (a
  // deliberate second copy of the modal's own test, per the note this comment
  // replaces). Both are gone.
  //
  // Restore both if DISPOSAL_ONLY_FIELDS is ever re-populated. Note what that
  // costs: the guard was two-part — `Array.isArray(activeSite?.site_modules)
  // && ...includes('DISPOSAL')` — because a site doc with no site_modules key
  // is normal and a bare .includes() on undefined throws. Any restoration
  // needs both halves back, and the same collapse happened in
  // FieldRecordModal.jsx, so there are two sites to restore, not one.

  // The field lists follow the CURRENT INV/ALL view, so a picker only ever
  // offers what that view can show. fieldsForView is the ONE view→field-set
  // mapping; columnsFields is that list and feeds the Sort, Filter and Group
  // pickers as-is (Bob P1 item 2a). MEDIA has a null key, belongs to no
  // manifest, appears in no list, and is therefore not hideable — a known
  // divergence, logged rather than solved here.
  //
  // The scrap trio (approved_scrap, marked, scrap_project_number) needs no
  // INV clause: all three are absent from INV_KEYS, so the view filter
  // already excludes them and they stay ALL-only for free — the same
  // free-ride the record modal relies on.
  const columnsFields = useMemo(() => fieldsForView(fieldView), [fieldView]);

  // The Columns picker ALONE gets a second list (P1-2 piece 1): columnsFields
  // plus, in ALL, the six child-value columns (PART NUMBER 1-3, ASSET TAG
  // 1-3), so they can be hidden. Picker and grid no longer share one field list
  // "by construction"; what keeps their order identical now is that both pass
  // through the same withChildColumns (here and in columnsForView). The six are
  // NOT fields — a slot cannot be sorted, filtered or grouped on — so Sort,
  // Filter and Group keep columnsFields.
  const columnsPickerFields = useMemo(
    () => withChildColumns(columnsFields, fieldView),
    [columnsFields, fieldView],
  );

  // ONE code path with Android hardware back, which is the entire point of
  // Unit D. Both pop the site entry; the teardown effect above clears
  // activeSite and records off the sentinel's disappearance. The three
  // setters that used to live here are gone — a button that tore state down
  // directly could not stay in step with a pop that did not, and that
  // divergence WAS the bug: the pop moved history while the button moved
  // state, so the platform back popped the /field entry instead.
  //
  // .frl-back is the only caller (FieldRecordsList.jsx, threaded at the
  // onBack prop below); nothing else in the zone references onBack.
  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // ── Record modal, router-native (5.2 Phase 1, D-5.2-A) ──────────────────
  // Open = SAME-/field-path history push carrying only a state sentinel (the
  // URL never changes — SW-scope guardrail). isOpen derives from that state;
  // EVERY close path (X / backdrop / Escape / footer button / browser-back /
  // gesture-back) is navigate(-1) popping the sentinel. No popstate listener,
  // no pushState, no history.back() cleanup — the pop is the whole mechanism.
  const modalRecordId = location.state?.tmgFieldModalRecordId ?? null;
  const modalRecord = modalRecordId
    ? records.find((r) => r.record_id === modalRecordId) || null
    : null;
  // 5.2b build-B-i: second sentinel — create-mode open, same router-native
  // back mechanism (D-5.2-A). No record id; the modal owns an in-memory draft.
  const modalCreate = location.state?.tmgFieldModalCreate === true;

  // Unit 6 P1 CONVENTION FIX. These two were the only pushes in the zone that
  // REPLACED location.state instead of spreading it — they predate the spread
  // convention that tmgFieldSheetField, tmgFieldMediaViewerIdx,
  // tmgFieldSyncSheet and tmgFieldColumnsOpen all follow. Behaviour is
  // identical from an empty stack (spreading {} adds nothing), which is why
  // the omission was invisible for four sentinels' worth of work.
  //
  // Required by Unit 6 P2: opening a record FROM the search overlay pushes
  // over an entry that already carries tmgFieldSearchOpen. Replacing state
  // would drop that sentinel, so the overlay would vanish behind the modal and
  // one back would land on a bare records view instead of returning to the
  // search the rep came from. `location.state` joins the deps for the same
  // reason it does in handleColumns — without it the closure captures a stale
  // state object and the spread silently drops whatever layer is underneath.
  const handleRowTap = useCallback((record) => {
    modalOpenIdRef.current = record.record_id; // P2-2 B: ahead of the push
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldModalRecordId: record.record_id },
    });
  }, [navigate, location.pathname, location.search, location.state]);

  const handleAddTool = useCallback(() => {
    // B-254 D-10: a create releases the pin. Held, it would treat the create modal as "a record
    // is open" and keep the new tool out of the pinned set, invisible until some other release.
    setPin(null);
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldModalCreate: true },
    });
  }, [navigate, location.pathname, location.search, location.state]);

  // P2-5 Phase 3 (B-198): Duplicate, from the record menu. Opens create mode
  // seeded from the record, as a push OVER the record's own entry (the
  // spread keeps tmgFieldModalRecordId beneath): Create or Close pops back to
  // the source record, so duplicating a family of fixtures is menu, Duplicate,
  // Create, again. The seed derives from location.state + records, so it is
  // the same on every render of the entry; the modal reads it once, at the
  // start of the create session.
  const duplicateOfId = modalCreate ? (location.state?.tmgFieldDuplicateOf ?? null) : null;
  const createSeed = useMemo(() => {
    if (!duplicateOfId) return null;
    const source = records.find((r) => r.record_id === duplicateOfId);
    return source ? buildDuplicateDraft(source, records, { siteId: activeSite?.site_id }) : null;
  }, [duplicateOfId, records, activeSite]);

  const handleDuplicate = useCallback((record) => {
    setPin(null); // B-254 D-10: Duplicate is a create too
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldModalCreate: true, tmgFieldDuplicateOf: record.record_id },
    });
  }, [navigate, location.pathname, location.search, location.state]);

  const handleModalClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // ── Columns modal, router-native (Unit 4 Phase 2) ───────────────────────
  // Sixth sentinel in the zone, same D-5.2-A / D-5.3-8 mechanism as the other
  // five: SAME-URL push (the URL never changes — field SW scope guardrail),
  // state SPREAD so the layer underneath survives onto the pushed entry,
  // isOpen derived from location.state and never from useState, and every
  // close path resolving through one navigate(-1).
  //
  // ANDROID HARDWARE BACK is the reason this is history-based rather than a
  // boolean. Back pops the entry, tmgFieldColumnsOpen goes away, the modal
  // unmounts — and NOTHING else moves. Bob's device makes this the most
  // exercised close path in the zone, so it is the one the design is built
  // around rather than patched for.
  //
  // ⚠️ WHY "NOTHING ELSE MOVES" IS STILL TRUE, ON A NEW BASIS (Unit D).
  // It used to hold trivially: the site<->records split was `view` useState,
  // which no pop could touch. That is no longer the case — the split IS now
  // history-derived, off tmgFieldSiteOpen (the 13th sentinel, declared at
  // :100+). The conclusion survives for a different reason: the site
  // sentinel is the BOTTOM of the stack, and every overlay push above it —
  // this one included — SPREADS location.state, so the entry beneath still
  // carries tmgFieldSiteOpen. Popping this modal restores that entry,
  // siteOpen stays true, the teardown effect's false-edge never fires, and
  // the records screen is still there behind the closed modal.
  //
  // What that makes load-bearing: the state SPREAD in every push. A push
  // that REPLACED state instead of spreading it would drop the site
  // sentinel, and closing that overlay would dump the rep back to the sites
  // list with their site torn down. handleUnsentTap (:900+) is the one push
  // in this file that does not spread wholesale, and it rest-destructures a
  // single named key, so the site sentinel rides through it untouched.
  const columnsOpen = location.state?.tmgFieldColumnsOpen === true;

  const handleColumns = useCallback(() => {
    if (columnsOpen) return; // already up — never double-push the sentinel
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldColumnsOpen: true },
    });
  }, [navigate, location.pathname, location.search, location.state, columnsOpen]);

  const handleColumnsClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // ── Search overlay, router-native (Unit 6 Phase 1) ──────────────────────
  // SEVENTH sentinel. handleColumns cloned verbatim — same-URL push
  // (pathname + search, so the URL never changes and the field SW's /field
  // scope holds), state SPREAD with the `|| {}` guard because location.state
  // is null on a fresh entry and spreading null throws, re-entrancy guard so a
  // double tap cannot need two backs, and location.state in the deps so the
  // spread never runs against a stale object.
  const searchOpen = location.state?.tmgFieldSearchOpen === true;

  const handleSearch = useCallback(() => {
    if (searchOpen) return; // already up — never double-push the sentinel
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldSearchOpen: true },
    });
  }, [navigate, location.pathname, location.search, location.state, searchOpen]);

  // ── View panel + Sort modal, router-native (Unit 5 Phase 1) ─────────────
  // EIGHTH and NINTH sentinels. handleSearch cloned verbatim, twice — same-URL
  // push so the field SW's /field scope holds, state SPREAD with the `|| {}`
  // guard because location.state is null on a fresh entry and spreading null
  // throws, re-entrancy guard so a double tap cannot need two backs, and
  // location.state in the deps so the spread never runs against a stale object.
  //
  // TWO sentinels rather than one with a mode, because they are two LAYERS: the
  // sort editor opens ON TOP of the View sheet, so one back closes the editor
  // and leaves the sheet standing, and a second back closes the sheet. The
  // spread is what carries tmgFieldViewPanelOpen onto the pushed entry — the
  // exact mechanism Unit 6 P2 needed for open-record-from-search, and the exact
  // thing a state-REPLACING push would have broken.
  const viewPanelOpen = location.state?.tmgFieldViewPanelOpen === true;

  const handleView = useCallback(() => {
    if (viewPanelOpen) return; // already up — never double-push the sentinel
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldViewPanelOpen: true },
    });
  }, [navigate, location.pathname, location.search, location.state, viewPanelOpen]);

  const handleViewClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const sortOpen = location.state?.tmgFieldSortOpen === true;

  const handleSortOpen = useCallback(() => {
    if (sortOpen) return; // already up — never double-push the sentinel
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldSortOpen: true },
    });
  }, [navigate, location.pathname, location.search, location.state, sortOpen]);

  const handleSortClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Apply = commit THEN pop, so the editor's commit path resolves through the
  // same single navigate(-1) that Cancel, ✕, Escape and hardware back do. The
  // modal itself never navigates — it hands up a draft and nothing else.
  const handleSortApply = useCallback((next) => {
    setSort(next);
    navigate(-1);
  }, [setSort, navigate]);

  // TENTH sentinel. handleSearch cloned verbatim again — same-URL push so the
  // field SW's /field scope holds, state SPREAD with the `|| {}` guard,
  // re-entrancy guard, location.state in the deps. It layers over
  // tmgFieldViewPanelOpen exactly as tmgFieldSortOpen does, so one back closes
  // the editor and leaves the View sheet standing.
  const filterOpen = location.state?.tmgFieldFilterOpen === true;

  const handleFilterOpen = useCallback(() => {
    if (filterOpen) return; // already up — never double-push the sentinel
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldFilterOpen: true },
    });
  }, [navigate, location.pathname, location.search, location.state, filterOpen]);

  const handleFilterClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Both halves commit together — see useFieldFilterState:20-24.
  const handleFilterApply = useCallback((nextFilter, nextLogic) => {
    setFilter(nextFilter, nextLogic);
    navigate(-1);
  }, [setFilter, navigate]);

  // ELEVENTH sentinel — the filter modal's enum value picker.
  //
  // INDEX-VALUED, not boolean: it carries WHICH condition's picker is up, so
  // it mirrors tmgFieldMediaViewerIdx (FieldRecordModal viewerIdx), the zone's
  // existing index-valued sentinel, and is read with `?? null` rather than
  // `=== true`. Condition 0 is a real, falsy index — `?? null` keeps it open
  // where a truthiness test would silently close it.
  //
  // WHY THIS IS A SENTINEL AND NOT LOCAL STATE. BottomSheetSelect in exactly
  // this role is already router-native in the record modal (tmgFieldSheetField,
  // FieldRecordModal openSheet), and for the reason this whole architecture is
  // history-based: ANDROID HARDWARE BACK (:267-272). Held in component state,
  // a back gesture inside the value picker would find nothing of its own to
  // pop and would take tmgFieldFilterOpen instead — closing the entire modal
  // and discarding the rep's draft. Same defect class as the Escape
  // double-fire, on the dismissal a rep uses most and the one we cannot test
  // before the 9/1 walkthrough.
  //
  // The SPREAD is what keeps the draft alive: tmgFieldFilterOpen rides onto
  // the pushed entry, so FieldFilterModal never unmounts while the picker is
  // up and its working copy survives the round trip.
  const filterSheetIndex = location.state?.tmgFieldFilterSheet ?? null;

  const handleFilterSheetOpen = useCallback((index) => {
    if (filterSheetIndex != null) return; // already up — never double-push
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldFilterSheet: index },
    });
  }, [navigate, location.pathname, location.search, location.state, filterSheetIndex]);

  const handleFilterSheetClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // TWELFTH sentinel. handleSortOpen cloned verbatim — same-URL push so the
  // field SW's /field scope holds, state SPREAD with the `|| {}` guard
  // because location.state is null on a fresh entry and spreading null
  // throws, re-entrancy guard so a double tap cannot need two backs, and
  // location.state in the deps so the spread never runs against a stale
  // object. It layers over tmgFieldViewPanelOpen exactly as tmgFieldSortOpen
  // and tmgFieldFilterOpen do, so one back closes the editor and leaves the
  // View sheet standing.
  const groupOpen = location.state?.tmgFieldGroupOpen === true;

  const handleGroupOpen = useCallback(() => {
    if (groupOpen) return; // already up — never double-push the sentinel
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldGroupOpen: true },
    });
  }, [navigate, location.pathname, location.search, location.state, groupOpen]);

  const handleGroupClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // Apply = commit THEN pop, the order Sort and Filter already use, so the
  // commit path resolves through the same single navigate(-1) that Cancel,
  // the ✕, Escape and hardware back do.
  const handleGroupApply = useCallback((next) => {
    setGroup(next);
    navigate(-1);
  }, [setGroup, navigate]);

  // The zero-result surface's escape hatch. NO navigate: this is reached from
  // the grid itself with no modal open and nothing on the history stack to
  // pop, so it commits and returns the rep to a populated grid in one tap.
  // The only committed clear in the unit — every other Clear is a draft edit.
  const handleClearFilter = useCallback(() => {
    clearFilter();
  }, [clearFilter]);

  // ── Recents (Unit 6 Phase 1) ────────────────────────────────────────────
  // IN MEMORY, NOT PERSISTED — and that is a decision, not a shortcut. The
  // hidden-column set earns localStorage because it is a dozen deliberate
  // choices a rep should not have to re-make each shift; a list of what they
  // last tapped is a within-session convenience, and persisting it would put
  // tool identifiers on the device with no expiry and no owner. `fieldView`
  // sets the same precedent one line at a time.
  //
  // Both lists: cap 5, newest first, de-duped CASE-INSENSITIVELY. Viewed rows
  // key on record_id (the only client-side unique — TOOL IDs are not unique,
  // duplicates must stay distinct); searched rows key on the folded term.
  const [recentViewed, setRecentViewed] = useState([]);
  const [recentSearched, setRecentSearched] = useState([]);

  const rememberViewed = useCallback((record) => {
    const id = record?.record_id;
    if (!id) return;
    // The label the overlay shows, resolved at REMEMBER time so a row whose
    // TOOL ID arrives later does not leave a stale recent behind.
    //
    // recordLabel is the shared chain, extracted from here. Its fourth term
    // (record_id) originated as this call site's `|| id` and is why a recents
    // row can never be blank — the label IS the affordance saying the row
    // exists and is tappable, so a blank one is an invisible control. The
    // record modal was the restatement that LACKED that term and rendered a
    // blank title (F-53); it now shares this one, so the two cannot drift.
    const label = recordLabel(record);
    setRecentViewed((prev) => {
      // Already at the head with the same label → return prev UNCHANGED so
      // React bails out of the re-render. This is what lets the effect below
      // carry full deps: it re-fires on every commit to the open record, and
      // every re-fire after the first is free.
      if (prev[0]?.record_id === id && prev[0]?.label === label) return prev;
      return [
        { record_id: id, label },
        ...prev.filter((r) => r.record_id !== id),
      ].slice(0, 5);
    });
  }, []);

  const rememberSearched = useCallback((term) => {
    const trimmed = String(term ?? '').trim();
    if (!trimmed) return; // an empty close commits nothing
    const folded = trimmed.toLowerCase();
    setRecentSearched((prev) => [
      trimmed,
      ...prev.filter((t) => t.toLowerCase() !== folded),
    ].slice(0, 5));
  }, []);

  const removeSearched = useCallback((term) => {
    const folded = String(term ?? '').toLowerCase();
    setRecentSearched((prev) => prev.filter((t) => t.toLowerCase() !== folded));
  }, []);

  // Pop only. The term is committed by the overlay's UNMOUNT cleanup, not
  // here. EVERY dismissal unmounts that component — iOS swipe-back, Android
  // hardware back, and Cancel — which is why unmount is the one commit site;
  // a close HANDLER would miss both back gestures, since neither runs one, and
  // Recently Searched would sit almost always empty. rememberSearched is
  // handed down as onCommitTerm.
  //
  // WITNESSED ON iOS ONLY. Android hardware back is a design target, untested
  // until the 9/1 walkthrough.
  const handleSearchClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // A recent-viewed tap opens that record. Reuses handleRowTap rather than
  // re-deriving the push, so the overlay and the grid cannot disagree about
  // how a record opens — and the 1d spread fix is what lets this land on top
  // of tmgFieldSearchOpen without dropping it.
  const handleRecentTap = useCallback((item) => {
    const row = records.find((r) => r.record_id === item.record_id);
    if (row) handleRowTap(row);
  }, [records, handleRowTap]);

  // "Viewed" is recorded when the record modal actually OPENS, not when a row
  // is tapped. An effect rather than a call inside handleRowTap for two
  // reasons: rememberViewed is declared below that handler, so naming it in
  // handleRowTap's dep array would be a TDZ reference at render time; and
  // keying on the opened record catches EVERY path in — grid tap, recent tap,
  // and a forward navigation that restores the sentinel — instead of only the
  // one handler that happens to exist today.
  useEffect(() => {
    if (modalRecord) rememberViewed(modalRecord);
  }, [modalRecord, rememberViewed]);

  // Phase 2: a field commit returns the updated row — refresh it in place so
  // the modal (and grid behind it) re-render current working values.
  // 5.2b build-B-ii: UPSERT — create-mode Save hands back a brand-new
  // pending-* row that isn't in the list yet; append it (optimistic), it
  // re-keys to the real tool id on the next drain cycle + re-Load.
  const handleRecordChange = useCallback((updated) => {
    setRecords((prev) => (prev.some((r) => r.record_id === updated.record_id)
      ? prev.map((r) => (r.record_id === updated.record_id ? updated : r))
      : [...prev, updated]));
  }, []);

  // B-254 Phase 2: a tap on FIX / FOUND / ADDED in the grid or the search overlay. The order
  // and the outcomes live in tapFlag; this only hands it the host's row update.
  // Phase 3: the first tap pins the view on screen, before the write can re-derive it. A pin
  // that already applies is kept; one made under another view key is replaced.
  const handleFlagTap = useCallback((record, key) => {
    const src = pinSourceRef.current;
    setPin((prev) => (prev && prev.key === src.key ? prev : makePin(src.key, src.rows, src.tree)));
    return tapFlag(record, key, handleRecordChange);
  }, [handleRecordChange]);

  // P2-2 B: reconcile re-keyed a pending-* row onto its server id and wrote
  // the real row to Dexie: the server's create stamp (added_by_tmg / found)
  // in baseline, the rep's FOUND / ADDED in working where the rep set them.
  // The list is a one-shot snapshot, so without this the grid keeps the
  // pending copy until the next site entry. Deliberately NOT
  // handleRecordChange: that upserts by record_id, and the id CHANGES here,
  // so it would append a second row beside the stale one.
  //
  //   1. Nothing to deliver -> no setRecords at all (no wasted render).
  //   2. A site load in flight -> hold the pairs; flushRekeyStash replays them.
  //   3. Only rows of the site on screen; a late reconcile after the rep
  //      moved sites must not drop a foreign row into this grid.
  //   4. The row open in the record modal -> held in deferredRekeyRef and
  //      released by the modal-id effect below; the rest of the batch
  //      applies now. The modal keeps its prop row and shows the re-keyed
  //      notice, exactly as before.
  //   5. ONE setRecords for the batch. Per pair: replace IN PLACE by
  //      pending_id; else in place by the real id (already delivered — this
  //      is what makes a replay a no-op); else append. The append is the
  //      approved fallback for exits 5b/5c/5d, whose rows were never pushed.
  //      In place, never remove-then-append: the row must not move. A
  //      released hold (replaceOnly) never appends — see the effect.
  const applyRekeyed = useCallback((pairs) => {
    if (!Array.isArray(pairs) || pairs.length === 0) return;
    if (rekeyStashRef.current) {
      rekeyStashRef.current.push(...pairs);
      return;
    }
    const siteId = liveSiteIdRef.current;
    const mine = pairs.filter((p) => p && p.record && siteId != null && p.record.site_id === siteId);
    if (mine.length === 0) return;
    const openId = modalOpenIdRef.current;
    const ready = [];
    for (const p of mine) {
      if (openId != null && p.pending_id === openId) deferredRekeyRef.current = p;
      else ready.push(p);
    }
    if (ready.length === 0) return;
    // B-254 Phase 3: the pin follows a re-key HERE, for exactly the pairs this batch applies,
    // never on arrival. A stashed pair (a site load in flight) or a deferred one (the row open
    // in the modal) returned above, so the pin keeps the pending id exactly while `records`
    // does, and both move in this same batch when the pair is applied. Not inside the records
    // updater: an updater must stay pure. The updater's own bail-outs leave nothing to show
    // either way: a site change has released the pin, and a gone row resolves to nothing under
    // either id.
    setPin((prev) => remapPin(prev, ready));
    setRecords((prev) => {
      // The site can change between this call and the update running (the
      // teardown effect clears the ref synchronously); never write into a
      // list that is no longer this site's.
      if (liveSiteIdRef.current !== siteId) return prev;
      const next = prev.slice();
      let changed = false;
      for (const { pending_id: pendingId, record, replaceOnly } of ready) {
        let i = next.findIndex((r) => r.record_id === pendingId);
        if (i === -1) i = next.findIndex((r) => r.record_id === record.record_id);
        if (i === -1 && replaceOnly) continue; // released hold, row gone: dropped
        if (i === -1) next.push(record);
        else next[i] = record;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, []);
  applyRekeyedRef.current = applyRekeyed;

  // P2-2 B: the modal's record id moved (closed, or opened another record).
  // Sync the ref FIRST, so the release below is not held again, then release
  // a held re-key whose row is no longer the open one. Through applyRekeyed,
  // so a site load in flight stashes it like any other pair.
  // REPLACE-ONLY: the held row WAS in the list. If it is gone now, a Discard
  // or a teardown removed it on purpose, and an append would resurrect it.
  // Dexie has the real row either way; the next site entry reads it fresh.
  // Declared after the teardown effect, so on a commit that fires both the
  // teardown clears the hold first.
  useEffect(() => {
    modalOpenIdRef.current = modalRecordId;
    const held = deferredRekeyRef.current;
    if (held && held.pending_id !== modalRecordId) {
      deferredRekeyRef.current = null;
      applyRekeyed([{ ...held, replaceOnly: true }]);
    }
  }, [modalRecordId, applyRekeyed]);

  // Field cancel Phase 2: a Discard removed a pending- row from fieldDb. The
  // host list is a one-shot snapshot (see F-43 above), so without this the
  // modal would close onto a row still on screen — the ghost-row shape.
  // navigate(-1) FIRST: modalRecord derives from records.find, so filtering
  // alone makes the modal early-return null with its history sentinel still
  // pushed. (navigate(-1) is an async history pop, so the filter still
  // renders a frame before the pop lands — harmless, the pop is already
  // requested; what must never happen is a filter WITHOUT the pop.)
  // Recently-viewed may still hold this record_id; handleRecentTap finds no
  // row and does nothing — left alone deliberately.
  //
  // Split (Phase 3): dropRecord FILTERS ONLY and is what the sync sheet gets
  // — the sheet has its own history entry, and the modal's navigate(-1)
  // would pop THAT and close the sheet. handleRecordRemoved is the modal's:
  // pop first, then drop.
  const dropRecord = useCallback((recordId) => {
    setRecords((prev) => prev.filter((r) => r.record_id !== recordId));
  }, []);

  const handleRecordRemoved = useCallback((recordId) => {
    navigate(-1);
    dropRecord(recordId);
  }, [navigate, dropRecord]);

  // ── Sync-detail sheet (5.4 A-1 Gate 2) — own sentinel, SPREAD over any
  // existing state (D-5.3-8 precedent: back closes the sheet layer only).
  const syncSheetOpen = location.state?.tmgFieldSyncSheet === true;

  const handleSyncChipTap = useCallback(() => {
    if (syncSheetOpen) return; // already up — never double-push the sentinel
    navigate(location.pathname + location.search, {
      state: { ...(location.state || {}), tmgFieldSyncSheet: true },
    });
  }, [navigate, location.pathname, location.search, location.state, syncSheetOpen]);

  const handleSyncSheetClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  // F-45: open a record FROM the sync sheet.
  //
  // 🔴 ONE navigation, and the sentinel swap is why. FieldSyncSheet is
  // own-chrome at a TRUE root z-index of 101. FieldRecordModal renders through
  // ModalShell, whose backdrop is position:fixed at z-index 100 — that backdrop
  // ESTABLISHES A STACKING CONTEXT, so the dialog's own 101 never competes at
  // root and the modal's effective root height is 100. A record modal opened
  // while the sheet is still mounted paints UNDERNEATH it. So the sheet must be
  // gone in the same navigation that opens the modal: drop tmgFieldSyncSheet
  // and add tmgFieldModalRecordId together, never as two pushes, and never by
  // hoping the modal out-paints the sheet.
  //
  // A PUSH, not a replace. The two requirements are separate and each has its
  // own mechanism: the sheet must not be MOUNTED under the modal — the dropped
  // sentinel handles that, and it is what the stacking-context note above is
  // about — but it should be RETURNED TO on close, which is what the push
  // buys. The modal's own navigate(-1) then pops back to the entry still
  // carrying tmgFieldSyncSheet, so a rep clearing several unsent records gets
  // sheet → record → Save → close → sheet, instead of being dropped back to
  // the grid and having to re-open the sheet for every record.
  //
  // The `if (!row) return` guard is handleRecentTap's precedent (:535+) and is
  // load-bearing here, not defensive: `records` holds ONLY the loaded site's
  // rows, so a cross-site unsent row is legitimately absent. That absence IS
  // the tappability rule — the sheet renders those rows as plain text and never
  // calls this, and if one ever did, this returns instead of pushing a sentinel
  // that would resolve to a null modalRecord and open nothing.
  const handleUnsentTap = useCallback((item) => {
    const row = records.find((r) => r.record_id === item.record_id);
    if (!row) return;
    modalOpenIdRef.current = row.record_id; // P2-2 B: ahead of the push
    const { tmgFieldSyncSheet, ...restState } = location.state || {};
    navigate(location.pathname + location.search, {
      state: { ...restState, tmgFieldModalRecordId: row.record_id },
    });
  }, [records, navigate, location.pathname, location.search, location.state]);

  // Chip + sheet render OUTSIDE the view branch — visible in both views.
  // B-224: loadFailed follows staleRecords, so it stays up after the rep backs
  // out to the sites list, where the chip is the fact's only carrier.
  return (
    <>
      <FieldSyncChip counts={queue} loadFailed={staleRecords !== null} onTap={handleSyncChipTap} />
      {view === 'records' ? (
        <>
          <FieldRecordsList
            site={activeSite}
            /* Unit 5 P1: the SORTED view, not the raw snapshot. Everything
               else in this file that touches `records` does an id lookup
               (modalRecord, handleRecentTap) or an upsert, none of which
               depends on order — so this is the one consumer that changes. */
            records={shownRecords}
            /* Unit 5 P2 — the UNFILTERED total for the "N of M" count bar and
               the filtered empty state. The list cannot derive it: by the time
               `records` reaches it, it is already the filtered array. */
            totalRecords={records.length}
            pruneDryRun={pruneDryRunOptIn ? pruneDryRun : null}
            /* B-224: the state is this page's; the list renders the banner,
               because it owns the 100dvh column the banner has to fit in. */
            staleNotice={staleRecords}
            filterActive={filter.length > 0}
            onClearFilter={handleClearFilter}
            dirtyIds={dirtyIds}
            onBack={handleBack}
            onRowTap={handleRowTap}
            onFlagTap={handleFlagTap}
            onAddTool={handleAddTool}
            onColumns={handleColumns}
            onSearch={handleSearch}
            onView={handleView}
            hidden={hidden}
            /* Bob P1 item 2a: the grid's columns follow the view, and the
               grid header is now the ONLY place the view is set. */
            fieldView={fieldView}
            onFieldViewChange={setFieldView}
            /* Unit 5 P3c — the BUCKET TREE, or null when no grouping is
               set. Never the flat array: see the groupTree memo on why the
               grid must not have to type-sniff this prop. */
            groups={shownTree}
            collapsedGroups={collapsedGroups}
            onToggleGroup={handleToggleGroup}
          />
          <FieldRecordModal
            mode={modalCreate ? 'create' : 'edit'}
            record={modalRecord}
            site={activeSite}
            callerEmail={callerEmail}
            isOpen={modalRecord != null || modalCreate}
            onClose={handleModalClose}
            onRecordChange={handleRecordChange}
            onRecordRemoved={handleRecordRemoved}
            /* The ONE useFieldDrain instance's cycle flag. Never a second
               useFieldDrain() in the modal: the module latch blocks a second
               instance from cycling, so its syncing would read false forever. */
            syncing={queue.syncing}
            /* The view still governs the form; it is no longer SET from
               inside it — no setter is passed (Bob P1 item 2a, fault 2). */
            fieldView={fieldView}
            hidden={hidden}
            /* RAW `records`, as FieldFilterModal gets them: the location
               picker offers every value this site holds, not the filtered
               view's subset. */
            records={records}
            /* P2-5 Phase 3: the record menu's Duplicate, and the seed for the
               create session it opens (null for an ordinary Add). */
            onDuplicate={handleDuplicate}
            createSeed={createSeed}
          />
        </>
      ) : (
        <div data-testid="field-zone" style={{ padding: '1.5rem', fontFamily: 'system-ui, sans-serif' }}>
          {/* F-38: title + eyebrow, no network claim. The eyebrow's
              numbers are NOT invented — they mirror the records view's
              .frl-site-name / .frl-record-count pairing (12px,
              --color-text-secondary) with the label letter-spacing this
              feature already uses on .frl-table thead th (0.03em). The h1
              keeps its rendered size; only its margin is zeroed so the
              eyebrow seats under it, and the wrapper restores the vertical
              gap the h1's default bottom margin used to provide. Colour
              goes through a token even inline — the design-system
              mandate does not stop at a stylesheet boundary (the two error
              lines below set that precedent). */}
          <div data-testid="field-header" style={{ marginBottom: '1rem' }}>
            <h1 style={{ margin: 0 }}>ToolSight</h1>
            <div
              style={{
                fontSize: 'var(--fz-text-micro)',
                letterSpacing: '0.03em',
                color: 'var(--color-text-secondary)',
              }}
            >
              SUPPLIER SITES
            </div>
          </div>
          {error && (
            <p data-testid="field-error" style={{ color: 'var(--color-danger, #b00)' }}>
              Load error: {error}
            </p>
          )}
          {loadError && (
            <p data-testid="field-load-error" style={{ color: 'var(--color-danger, #b00)' }}>
              {loadError}
            </p>
          )}
          <FieldSitesList sites={sites} loadingSiteId={loadingSiteId} onSelect={handleSelectSite} />
          {/* S5.7: the zone's only exit. Until now /field had no outbound
              control of any kind — no Link, no href, no absolute navigate — so
              once the three nav items were repointed here, a desktop user had no
              way back short of the browser's own Back button.
              navigate('/') and NOT navigate(-1): the FIRST /field history entry
              carries no sentinel, so a pop there leaves the SPA entirely instead
              of closing an overlay. DefaultRedirect then resolves '/' to
              /registry on the rail or /home on the launcher.
              Sites view ONLY — the records view has its own header row and its
              own back control (frl-back), and an exit beside it would put two
              competing "leave" affordances in one bar.
              Placed at the FOOT of the list deliberately: offline this exit
              lands on the authError panel, so it must not be a thumb-adjacent
              target at the top of the zone's primary screen.
              Styled inline against the same four tokens .frl-back uses, because
              this file imports no stylesheet at all and inline-with-tokens is the
              pattern its whole header already follows. One fidelity gap, taken
              knowingly: an :active press state cannot be expressed inline, so
              this control lacks the tap feedback .frl-back has. */}
          <button
            type="button"
            data-testid="field-exit"
            aria-label="Exit Field Zone"
            onClick={() => navigate('/')}
            style={{
              display: 'block',
              marginTop: '1.5rem',
              background: 'var(--color-bg-subtle)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              padding: '8px 14px',
              fontSize: 'var(--fz-text-input)',
              fontWeight: 600,
              color: 'var(--color-text)',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            Exit Field Zone
          </button>
        </div>
      )}
      {/* F-40b: the sheet runs no useFieldDrain of its own, so dirtyCount is
          threaded from the one this page already holds — one subscription,
          not a second competing with it. */}
      <FieldSyncSheet
        isOpen={syncSheetOpen}
        onClose={handleSyncSheetClose}
        onRecordChange={handleRecordChange}
        onRecordRemoved={dropRecord}
        callerEmail={callerEmail}
        dirtyCount={queue.dirtyCount}
        halted={queue.halted}
        networkDown={networkDown}
        unsentItems={unsentItems}
        activeSiteId={activeSiteId}
        onUnsentTap={handleUnsentTap}
      />
      {/* Fragment level like the chip and sheet, but MOUNT-gated rather than
          isOpen-gated: the component takes no isOpen prop, so the sentinel is
          the whole gate. Unmounting on close also means the picker never
          holds stale `fields` from a view the rep has since switched away
          from. */}
      {columnsOpen && (
        <FieldColumnsModal
          fields={columnsPickerFields}
          hidden={hidden}
          onToggle={toggle}
          onShowAll={showAll}
          onHideAll={hideAll}
          onClose={handleColumnsClose}
        />
      )}
      {/* Unit 6 P1 — mount-gated on the sentinel, matching the Columns
          pattern: the overlay takes no isOpen prop, so tmgFieldSearchOpen IS
          the gate. Unmounting on close also discards the in-overlay query,
          which is why the committed term has to travel up through onClose
          rather than being read off the component afterwards. */}
      {searchOpen && (
        <FieldSearchOverlay
          records={records}
          /* visibleColumnsFor, not a second inline filter: the grid and the
             overlay must never disagree about which columns exist, and Unit 4
             already learned that lesson with header-vs-body-vs-colSpan. The
             overlay's RESULTS respect hidden columns; its MATCHING does not.
             fieldView rides along so the results follow the view exactly as
             the grid behind them does (Bob P1 item 2a). */
          visibleColumns={visibleColumnsFor(hidden, fieldView)}
          dirtyIds={dirtyIds}
          /* The SAME handler the grid rows use. Tapping a result layers the
             record sentinel over tmgFieldSearchOpen — no close first, so one
             back returns to the results. */
          onRowTap={handleRowTap}
          onFlagTap={handleFlagTap}
          recentViewed={recentViewed}
          recentSearched={recentSearched}
          onRecentTap={handleRecentTap}
          onRemoveSearch={removeSearched}
          onCommitTerm={rememberSearched}
          onClose={handleSearchClose}
        />
      )}
      {/* Unit 5 P1 — mount-gated on the sentinel, the Columns/Search pattern:
          neither surface takes an isOpen prop, so the sentinel IS the gate.

          ⚠ ORDER IS LOAD-BEARING. .fvp-sheet and .fsm are BOTH root-level
          position:fixed at --z-modal (101) — equal height, so DOM order is the
          only tiebreak, and the sort editor has to paint over the sheet that
          opened it. Reversing these two mounts puts the editor UNDERNEATH with
          no symptom except "Sort does nothing", which is precisely the shape of
          the Unit 6 P2 overlay bug (session_closing_unit6_search_20260827b.md
          §2). There is no 102 in the ladder to express this with. */}
      {viewPanelOpen && (
        <FieldViewPanel
          onFilter={handleFilterOpen}
          onSort={handleSortOpen}
          onGroup={handleGroupOpen}
          filterCount={filter.length}
          sortCount={sort.length}
          groupCount={groupCount(group)}
          /* The panel owns Escape only while it is the topmost layer — with an
             editor up, that editor owns it. See FieldViewPanel:107-119. */
          escapeEnabled={!sortOpen && !filterOpen && !groupOpen}
          onClose={handleViewClose}
        />
      )}
      {sortOpen && (
        <FieldSortModal
          /* The SAME view-scoped list the Columns picker gets, so the two
             cannot disagree about which fields exist in the current INV/ALL
             view. The picker splits it visible-then-hidden; the STORED sort is
             validated against the wider all-views union in useFieldSortState,
             so flipping INV↔ALL cannot silently delete a saved level. */
          fields={columnsFields}
          hidden={hidden}
          sort={sort}
          onApply={handleSortApply}
          onClose={handleSortClose}
        />
      )}
      {filterOpen && (
        <FieldFilterModal
          fields={columnsFields}
          hidden={hidden}
          /* RAW `records`, not displayedRecords. The enum value picker offers
             the values PRESENT ON THIS SITE, and narrowing that list by the
             filter being edited would make each condition erase its own
             options — pick a value, reopen, and everything else has vanished. */
          records={records}
          filter={filter}
          filterLogic={filterLogic}
          /* The value picker's sentinel, owned here like every other. The
             modal holds NO open-state of its own for it. */
          sheetIndex={filterSheetIndex}
          onSheetOpen={handleFilterSheetOpen}
          onSheetClose={handleFilterSheetClose}
          onApply={handleFilterApply}
          onClose={handleFilterClose}
        />
      )}
      {groupOpen && (
        <FieldGroupModal
          /* The SAME view-scoped list Sort, Filter and the Columns picker
             get, so no two of them can disagree about which fields exist in
             the current view. The STORED grouping is validated against the
             wider all-views union in useFieldGroupState, so flipping the
             view cannot silently delete a saved grouping. */
          fields={columnsFields}
          hidden={hidden}
          group={group}
          onApply={handleGroupApply}
          onClose={handleGroupClose}
        />
      )}
      {/* Fragment level, like the chip and sheet — visible in BOTH views. */}
      {/* A1.5: five taps here toggle the prune dry-run header line on this
          device. touch-action: manipulation keeps iOS from reading the taps as
          a double-tap zoom. */}
      <p data-testid="field-build" onClick={handleStampTap} style={{ padding: '0 1.5rem 1rem', fontFamily: 'system-ui, sans-serif', fontSize: 'var(--fz-text-micro)', color: 'var(--color-text-muted, #666)', touchAction: 'manipulation' }}>
        Build: {BUILD_SHA}{BUILD_DIRTY ? '+dirty' : ''}{pruneDryRunOptIn ? ' · dry run on' : ''}
      </p>
    </>
  );
}
